import { SubscriptionUsageMeter } from 'features/subscription/services/subscription-service';
import { PlaceUsage, SeatPool } from 'features/subscription/services/seat-service';

type Totals = Pick<SubscriptionUsageMeter, 'included' | 'used' | 'overage'>;

/**
 * Usage meters built from every seat the organization bought. Every Klax plan is user-wise, so
 * there is no organization subscription to read: the organization's figure is the sum of its
 * seats, and a person's is the seat they hold.
 *
 * Utilities writes a seat's usage row at its first charge, so a seat nobody has used yet has no
 * row. Its allowance still counts: what a seat includes comes from the plan's own terms, and a
 * seat without a row has used nothing. A plan waiting for its first payment grants nothing yet.
 *
 * Rows come out in the shape the credits card reads, with an empty `userId` for the organization.
 */
export const seatUsageMeters = (pools: SeatPool[]): SubscriptionUsageMeter[] => {
  const meters = new Map<string, SubscriptionUsageMeter>();

  const add = (
    userId: string,
    terms: { meterKey: string; unitLabel: string; quantityScale: number },
    amount: Totals,
    period: { start: string; end: string }
  ) => {
    const key = `${userId}|${terms.meterKey}`;
    const meter = meters.get(key);
    if (!meter) {
      meters.set(key, {
        allowed: true,
        userId,
        meterKey: terms.meterKey,
        unitLabel: terms.unitLabel,
        periodKey: '',
        periodStartUtc: period.start,
        periodEndUtc: period.end,
        quantityScale: terms.quantityScale,
        remaining: 0,
        replayed: false,
        projection: 0,
        ...amount,
      });
      return;
    }
    meter.included += amount.included;
    meter.used += amount.used;
    meter.overage += amount.overage;
    if (period.start && (!meter.periodStartUtc || period.start < meter.periodStartUtc))
      meter.periodStartUtc = period.start;
    if (period.end > meter.periodEndUtc) meter.periodEndUtc = period.end;
  };

  for (const pool of pools) {
    if (pool.subscription.status === 'Incomplete') continue;
    const rows = pool.members.usage ?? [];
    const period = {
      start: pool.subscription.currentPeriodStartUtc ?? '',
      end: pool.subscription.currentPeriodEndUtc ?? rows[0]?.periodEndUtc ?? '',
    };
    const terms = pool.subscription.meters?.length
      ? pool.subscription.meters
      : rows.map((row) => ({ ...row, includedQuantity: row.included }));
    const seen = new Set<string>();

    for (const meter of terms) {
      if (seen.has(meter.meterKey)) continue;
      seen.add(meter.meterKey);
      const meterRows = rows.filter((row) => row.meterKey === meter.meterKey);
      const rowOf = (userId: string): PlaceUsage | undefined =>
        meterRows.find((row) => row.userId === userId);
      const sum = (key: 'used' | 'overage') =>
        meterRows.reduce((total, row) => total + row[key], 0);

      add(
        '',
        meter,
        {
          included: meter.includedQuantity * pool.members.purchased,
          used: sum('used'),
          overage: sum('overage'),
        },
        period
      );

      for (const { userId } of pool.members.seats) {
        const row = rowOf(userId);
        add(
          userId,
          meter,
          {
            included: row?.included ?? meter.includedQuantity,
            used: row?.used ?? 0,
            overage: row?.overage ?? 0,
          },
          period
        );
      }
    }
  }

  return [...meters.values()].map((meter) => ({
    ...meter,
    remaining: Math.max(0, meter.included - meter.used),
    projection: meter.used,
    allowed: meter.overage <= 0,
  }));
};
